(function () {
  // ---------------------------------------------------------------------------
  // ACE IRIS — v5, 30/08/2026.
  //
  // Deux modifications par rapport au fichier d'origine d'Horizon, et deux
  // seulement. Tout le reste (garde-fous navigateur, blocage de rendu) est
  // inchange.
  //
  //  A. UN SEUL TYPE DE TRANSITION. Horizon lisait un attribut
  //     data-view-transition-triggered pose par les cartes produit et emettait
  //     alors un type different, `product-image-transition`, avec sa propre
  //     choregraphie (morphing d'image de 250 ms). Un clic sur une carte produit
  //     ne donnait donc pas la meme transition qu'un clic sur un lien de menu.
  //     On emet desormais toujours `page-navigation` : une seule logique, un
  //     seul timing, quel que soit le point de depart et le point d'arrivee.
  //     Les attributs residuels sont toujours nettoyes, pour qu'aucun nom de
  //     transition en double ne subsiste dans le document.
  //
  //  B. PLUS D'ANNULATION SUR INTERACTION. Horizon coupait net la transition en
  //     cours au premier `pointerdown` ou `keydown` (protection de l'INP). En
  //     pratique, un simple mouvement de souris avec le doigt sur le clic, ou
  //     une navigation rapide entre plusieurs pages, coupait l'animation au
  //     milieu : c'est l'une des causes du « parfois beaucoup trop rapide ».
  //     La transition dure 500 ms et n'ajoute aucune latence avant la
  //     navigation ; on la laisse toujours aller a son terme.
  // ---------------------------------------------------------------------------

  // Reports on Meta's in-app browsers (Facebook, Instagram) that fail to paint during
  // cross-document (MPA) View Transitions implementation that can freeze or
  // white-screen the storefront on navigation. June 2026 testing.
  // Remove check if every resolved.
  if (isMetaInAppBrowser()) {
    disableCrossDocumentViewTransitions();
  }

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  /**
   * Ace Iris : retire tout nom de transition residuel pose par un composant
   * (galerie de la fiche produit, carte produit). Aucun de ces noms ne doit
   * survivre a une navigation, sans quoi deux elements pourraient porter le
   * meme nom et le navigateur abandonnerait la transition.
   */
  function clearComponentTransitionNames() {
    document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
      element.removeAttribute('data-view-transition-type');
    });
    document.querySelectorAll('[data-view-transition-triggered]').forEach((element) => {
      element.removeAttribute('data-view-transition-triggered');
    });
  }

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    clearComponentTransitionNames();

    // Ace Iris : un seul type, toujours le meme.
    viewTransition.types.clear();
    viewTransition.types.add('page-navigation');

    try {
      sessionStorage.removeItem('custom-transition-type');
    } catch (error) {
      /* sessionStorage indisponible : sans consequence ici. */
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Ace Iris : le document d'arrivee joue exactement la meme animation que le
    // document de depart, quel que soit le lien qui a ete clique.
    viewTransition.types.clear();
    viewTransition.types.add('page-navigation');

    // Le document d'arrivee est neuf : rien a nettoyer dans le DOM. On purge
    // seulement la cle laissee par l'ancien mecanisme d'Horizon.
    idleCallback(() => {
      try {
        sessionStorage.removeItem('custom-transition-type');
      } catch (error) {
        /* sessionStorage indisponible : sans consequence ici. */
      }
    });
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return (
      !(viewTransition instanceof ViewTransition) ||
      isLowPowerDevice() ||
      prefersReducedMotion() ||
      isMetaInAppBrowser()
    );
  }

  /**
   * Detect Facebook / Instagram in-app browsers (Meta WebView).
   *
   * Meta's in-app browsers expose identifying tokens in the user-agent:
   *   - Facebook: `FBAN`, `FBAV`, `FB_IAB`, `FBIOS`
   *   - Instagram: `Instagram`
   *
   * We can't import this from utilities.js here (this file runs before modules),
   * so the equivalent `isMetaInAppBrowser()` in utilities.js must be kept in sync.
   * @param {string} [userAgent=navigator.userAgent] - User-agent string to test.
   * @returns {boolean} True if running inside a Facebook/Instagram in-app browser.
   */
  function isMetaInAppBrowser(userAgent = navigator.userAgent) {
    return /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram)\b/i.test(userAgent || '');
  }

  /**
   * Disable cross-document (MPA) View Transitions for the current document.
   *
   * Overrides the standard `@view-transition { navigation: auto }` CSS opt-in
   * and drops the render-blocking `<link rel="expect">` so a stalled transition
   * can never hold first paint (the white-screen symptom).
   */
  function disableCrossDocumentViewTransitions() {
    // 1. Override the standard `@view-transition` opt-in. A later `@view-transition`
    //    rule wins the cascade, so `navigation: none` here defeats base.css.
    const style = document.createElement('style');
    style.textContent = '@view-transition { navigation: none; }';
    (document.head || document.documentElement).appendChild(style);

    // 2. Never let the render blocker hold first paint on these browsers.
    document.getElementById('view-transition-render-blocker')?.remove();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
